<template>
  <dx-modal
    :value="show"
    :closable="!discarding"
    @input="close"
    size="sm"
    id="discard-signature-modal"
  >
    <template v-slot:header>
      <div class="discard-signature-header">
        <div class="discard-signature-icon">
          <fa-icon :icon="['fal', 'undo']" />
        </div>
        <div class="discard-signature-heading">
          <div class="discard-signature-title">Descartar assinatura digital?</div>
          <div class="discard-signature-subtitle">
            A assinatura digital {{ documentInfo.reference }} será descartada.
          </div>
        </div>
      </div>
    </template>

    <div class="discard-signature-consequences">
      <div
        class="discard-signature-consequence"
        v-for="item in consequences"
        :key="item.icon"
      >
        <div class="discard-signature-consequence-icon">
          <fa-icon :icon="['fal', item.icon]" />
        </div>
        <div class="discard-signature-consequence-text">
          <div class="discard-signature-consequence-title">{{ item.title }}</div>
          <div class="discard-signature-consequence-description">{{ item.description }}</div>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <button
        type="button"
        class="btn btn-error btn-icon btn-icon-left mr-1"
        :class="{ loading: discarding }"
        :disabled="discarding"
        @click="confirm"
      ><fa-icon :icon="['fal', 'undo']" />Descartar assinatura</button>
      <button
        type="button"
        class="btn"
        :disabled="discarding"
        @click="close"
      >Cancelar</button>
    </template>
  </dx-modal>
</template>

<script>
const documents = {
  receita: {
    reference: 'desta receita',
    editable: 'A receita volta a ficar editável',
    resign: 'Para ter validade, ela precisa ser assinada novamente.',
  },
  impresso: {
    reference: 'deste impresso',
    editable: 'O impresso volta a ficar editável',
    resign: 'Para ter validade, ele precisa ser assinado novamente.',
  },
  exame: {
    reference: 'desta solicitação de exames',
    editable: 'A solicitação volta a ficar editável',
    resign: 'Para ter validade, ela precisa ser assinada novamente.',
  },
  internacao: {
    reference: 'desta solicitação de internação',
    editable: 'A solicitação volta a ficar editável',
    resign: 'Para ter validade, ela precisa ser assinada novamente.',
  },
  anamnese: {
    reference: 'desta anamnese',
    editable: 'O atendimento volta a poder ser alterado',
    resign: 'Para ter validade, a anamnese precisa ser assinada novamente.',
  },
};

const fallback = {
  reference: 'deste documento',
  editable: 'O documento volta a ficar editável',
  resign: 'Para ter validade, ele precisa ser assinado novamente.',
};

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    documentType: {
      type: String,
      default: '',
    },
    discard: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      discarding: false,
    };
  },
  computed: {
    documentInfo() {
      return documents[this.documentType] || fallback;
    },
    consequences() {
      return [
        {
          icon: 'archive',
          title: 'O documento assinado continua guardado',
          description: 'O PDF assinado não é apagado.',
        },
        {
          icon: 'ban',
          title: 'A assinatura deixa de valer',
          description: 'O documento assinado perde a validade e não deve mais ser utilizado.',
        },
        {
          icon: 'lock-open',
          title: this.documentInfo.editable,
          description: this.documentInfo.resign,
        },
      ];
    },
  },
  methods: {
    close() {
      if (this.discarding) {
        return;
      }
      this.$emit('close');
    },
    async confirm() {
      if (this.discarding) {
        return;
      }

      this.discarding = true;
      try {
        await this.discard();
        this.discarding = false;
        this.$emit('close');
      } catch (e) {
        this.discarding = false;
        this.$toast.error(e);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

#discard-signature-modal {
  .modal-container {
    max-width: 23rem;
  }
  .modal-header {
    padding: $layout-spacing-lg $layout-spacing-lg $layout-spacing;
  }
  .modal-body {
    padding: $layout-spacing $layout-spacing-lg;
  }
  .modal-footer {
    padding: $layout-spacing $layout-spacing-lg $layout-spacing-lg;
  }
  .discard-signature-header {
    align-items: center;
    display: flex;
    gap: .6rem;
  }
  .discard-signature-icon {
    align-items: center;
    background-color: #FDF2F2;
    border: $border-width solid #F1C4C4;
    border-radius: 50%;
    color: $error-color;
    display: flex;
    flex: 0 0 2rem;
    font-size: $font-size-lg;
    height: 2rem;
    justify-content: center;
  }
  .discard-signature-heading {
    min-width: 0;
  }
  .discard-signature-title {
    color: $gray-color-900;
    font-size: $font-size-lg;
    font-weight: 700;
    line-height: 1.25;
  }
  .discard-signature-subtitle {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: .15rem;
  }
  .discard-signature-consequences {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    overflow: hidden;
  }
  .discard-signature-consequence {
    align-items: flex-start;
    display: flex;
    gap: .6rem;
    padding: .6rem .7rem;
    & + .discard-signature-consequence {
      border-top: $border-width solid $border-color;
    }
  }
  .discard-signature-consequence-icon {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border-radius: 50%;
    color: $gray-color-dark;
    display: flex;
    flex: 0 0 1.4rem;
    height: 1.4rem;
    justify-content: center;
  }
  .discard-signature-consequence-text {
    flex: 1;
    min-width: 0;
    padding-top: .2rem;
  }
  .discard-signature-consequence-title {
    color: $gray-color-900;
    font-weight: 600;
    line-height: 1.4;
  }
  .discard-signature-consequence-description {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    line-height: 1.45;
    margin-top: .05rem;
  }
}
</style>
